import { cloneElement } from "react";
import type { TagData } from "@progress/kendo-react-dropdowns";

/**
 * Kendo's MultiSelect gives every selected chip a real tabIndex of 0 by
 * default, so pressing Tab stops on each selected chip in turn before it
 * finally reaches the next form field. Arrow-key navigation between chips
 * already works without real DOM focus (Kendo tracks the "current" chip
 * internally and shows it via the .k-focus class - see the chip rule in
 * kendo-overrides.css), so nothing is lost by taking chips out of the tab
 * order. Pass this to a MultiSelect's `tagRender` prop to do that.
 *
 * Each chip's own accessible name (it announced blank without this) is
 * fixed separately, via useMultiSelectChipListLabel - Kendo's Chip
 * component doesn't forward a generic aria-label prop through to the DOM,
 * so it has to be set directly on the rendered element instead of through
 * this render callback.
 *
 * Clicking a chip still announced blank even after that fix: Kendo calls
 * preventDefault() on every chip's mousedown (to stop its own blur/close
 * handling), which also blocks the browser's default "click focuses this
 * element" behavior - so a click never produces a real focus event for a
 * screen reader to react to, and it falls back to a stale cached read
 * instead of freshly querying the (now-correct) accessible name. Chip's
 * own onClick only stops propagation, so it's safe to still call it and
 * additionally force real focus - tabIndex=-1 still allows focus via
 * script, just not via Tab.
 */
export function multiSelectTagRender(
  _tagData: TagData,
  tag: React.ReactElement<any>,
) {
  const onChipClick = tag.props.onClick as
    | ((event: React.MouseEvent<HTMLElement>) => void)
    | undefined;
  return cloneElement(tag, {
    tabIndex: -1,
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      onChipClick?.(event);
      event.currentTarget.focus();
    },
  });
}
